import {
  FeedbackModelData,
  FeedbackKind,
  FeedbackResponseKind,
  AssociatedModelKind,
} from "@/store/feedback/types";
import { BaseSyncModelStore } from "@/store/sync/BaseSyncModelStore";
import { SyncModelKind, SyncUpdateValue, OptimisticSyncUpdate } from "@/store/sync/types";
import { FeedbackObservable } from "@/store/feedback/FeedbackObservable";
import { AppSubStoreArgs } from "@/store/types";
import { makeObservable } from "mobx";
import { FeedbackIndexes } from "@/store/feedback/FeedbackIndexes";
import { AppStore } from "@/store/AppStore";
import { Maybe } from "@/domains/common/types";
import { ProvideFeedbackOperation } from "@/store/sync/operations/feedback/ProvideFeedbackOperation";
import { Dexie } from "dexie";
import { searchFeedback } from "@/store/feedback/search-feedback";

export class AppStoreFeedbackStore extends BaseSyncModelStore<
  FeedbackObservable,
  FeedbackModelData
> {
  constructor(injectedDeps: AppSubStoreArgs) {
    super({ modelKind: SyncModelKind.Feedback, ...injectedDeps });
    makeObservable(this, {
      getFeedbackByAssociatedModelId: true,
      provideFeedback: true,
      createSyncModel: false,
      computeIndexes: false,
    });
  }

  createSyncModel(data: SyncUpdateValue<FeedbackModelData>) {
    return new FeedbackObservable({
      id: data.model_id,
      data,
      store: this.store,
    });
  }

  public computeIndexes({
    store,
    remoteData,
    optimisticUpdates,
  }: {
    store: AppStore;
    remoteData: Maybe<SyncUpdateValue<FeedbackModelData>>;
    optimisticUpdates: OptimisticSyncUpdate<FeedbackModelData>[];
  }): Record<string, unknown> {
    return new FeedbackIndexes({ store, remoteData, optimisticUpdates }).indexes;
  }

  public getFeedbackByAssociatedModelId = async ({
    associatedModelIds,
    associatedModelKind,
    feedbackKind,
  }: {
    associatedModelIds: string[];
    associatedModelKind: AssociatedModelKind;
    feedbackKind: FeedbackKind;
  }) => {
    return await Dexie.waitFor(
      searchFeedback({
        store: this.store,
        associatedModelIds,
        associatedModelKind,
        feedbackKind,
      })
    );
  };

  async provideFeedback({
    feedbackKind,
    associatedModelId,
    associatedModelKind,
    associatedContext,
    feedbackResponseKind,
    feedbackSelectedSuggestions,
    feedbackUserText,
    feedbackRespondedAt,
  }: {
    feedbackKind: FeedbackKind;
    associatedModelId: string | null;
    associatedModelKind: string | null;
    associatedContext?: Record<string, unknown>;
    feedbackResponseKind: FeedbackResponseKind;
    feedbackSelectedSuggestions?: string[];
    feedbackUserText?: string;
    feedbackRespondedAt: string;
  }) {
    await new ProvideFeedbackOperation({
      store: this.store,
      payload: {
        feedback_kind: feedbackKind,
        associated_model_id: associatedModelId,
        associated_model_kind: associatedModelKind,
        associated_context: associatedContext ?? {},
        feedback_response_kind: feedbackResponseKind,
        feedback_selected_suggestions: feedbackSelectedSuggestions ?? [],
        feedback_user_text: feedbackUserText ?? null,
        feedback_responded_at: feedbackRespondedAt,
      },
    }).execute();
  }
}
